/**
 * Mobile Navigation Lazy Loading
 * Improves site loading performance by deferring mobile navigation rendering
 */

class MobileNavLazyLoader {
  constructor() {
    this.mobileNavWrapper = document.querySelector(".mobile-nav-wrapper");
    this.isLoaded = false;
    this.observer = null;

    this.init();
  }

  init() {
    if (!this.mobileNavWrapper) return;

    // Only apply lazy loading on mobile devices
    if (window.innerWidth >= 990) {
      this.loadImmediately();
      return;
    }

    // Check if Intersection Observer is supported
    if ("IntersectionObserver" in window) {
      this.setupIntersectionObserver();
    } else {
      // Fallback for older browsers
      this.loadImmediately();
    }

    this._onMobileNavCssReady = () => {
      if (window.innerWidth >= 990) return;
      if (
        this.mobileNavWrapper &&
        this.mobileNavWrapper.getAttribute("data-lazy") === "true" &&
        !this.isLoaded
      ) {
        this.loadMobileNav();
      }
    };
    window.addEventListener("mobileNavCssReady", this._onMobileNavCssReady);
    requestAnimationFrame(() => {
      if (!document.documentElement.classList.contains("header-css-boot")) {
        this._onMobileNavCssReady();
      }
    });
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: "50px", // Start loading 50px before the element comes into view
      threshold: 0.1,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoaded) {
          this.loadMobileNav();
        }
      });
    }, options);

    this.observer.observe(this.mobileNavWrapper);
  }

  loadMobileNav() {
    if (this.isLoaded) return;

    this.isLoaded = true;

    // Add lazy-loaded class to trigger CSS animation
    this.mobileNavWrapper.classList.add("lazy-loaded");

    // After loading, set up scroll-based hide/show behavior on mobile
    this.setupScrollBehavior();

    // Disconnect observer since we no longer need it
    if (this.observer) {
      this.observer.disconnect();
    }

    // Dispatch custom event for other scripts that might need to know when mobile nav is loaded
    window.dispatchEvent(
      new CustomEvent("mobileNavLoaded", {
        detail: { element: this.mobileNavWrapper },
      })
    );
  }

  loadImmediately() {
    // For desktop or when lazy loading isn't needed
    this.isLoaded = true;
    this.mobileNavWrapper.classList.add("lazy-loaded");

    // Still set up scroll behavior if we're on mobile
    this.setupScrollBehavior();
  }

  // Method to manually trigger loading (useful for testing or specific scenarios)
  forceLoad() {
    this.loadMobileNav();
  }
}

// Add scroll-based hide/show behavior
MobileNavLazyLoader.prototype.setupScrollBehavior = function () {
  const nav = this.mobileNavWrapper;
  if (!nav) return;

  // Only apply on mobile
  const isMobile = () => window.innerWidth < 990;
  if (!isMobile()) return;

  let lastY = window.scrollY || 0;
  let ticking = false;
  // Ignore tiny scroll noise (trackpads / iOS)
  const DELTA_THRESHOLD = 6;
  // Match header.liquid mobile sticky (~80px) so nav stays until header “sticks”
  const STICKY_SYNC_Y = 80;

  const onScroll = () => {
    const currentY = window.scrollY || 0;
    const delta = currentY - lastY;

    if (currentY <= STICKY_SYNC_Y) {
      nav.classList.remove("is-hidden");
      lastY = currentY;
      return;
    }

    if (Math.abs(delta) < DELTA_THRESHOLD) return;

    if (delta > 0) {
      nav.classList.add("is-hidden");
    } else {
      nav.classList.remove("is-hidden");
    }

    lastY = currentY;
  };

  const onScrollRaf = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        onScroll();
        ticking = false;
      });
      ticking = true;
    }
  };

  // Clean previous listener if reinitialized
  if (this._scrollHandler) {
    window.removeEventListener("scroll", this._scrollHandler, { passive: true });
  }

  this._scrollHandler = onScrollRaf;
  window.addEventListener("scroll", this._scrollHandler, { passive: true });

  // Ensure correct state on load
  if ((window.scrollY || 0) <= STICKY_SYNC_Y) {
    nav.classList.remove("is-hidden");
  }

  // Also update behavior on resize transitions across mobile/desktop
  const onResize = () => {
    if (!isMobile()) {
      nav.classList.remove("is-hidden");
    }
  };
  if (this._resizeHandler) {
    window.removeEventListener("resize", this._resizeHandler);
  }
  this._resizeHandler = onResize;
  window.addEventListener("resize", this._resizeHandler);
};

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new MobileNavLazyLoader();
});

// Reinitialize on window resize to handle orientation changes
window.addEventListener("resize", () => {
  // Debounce resize events
  clearTimeout(window.mobileNavResizeTimeout);
  window.mobileNavResizeTimeout = setTimeout(() => {
    // Only reinitialize if we're switching between mobile/desktop
    const isMobile = window.innerWidth < 990;
    const currentIsMobile =
      document.querySelector(".mobile-nav-wrapper.lazy-loaded") === null;

    if (isMobile !== currentIsMobile) {
      // Reinitialize the lazy loader instead of reloading the page
      const mobileNavWrapper = document.querySelector(".mobile-nav-wrapper");
      if (mobileNavWrapper) {
        // Remove existing lazy-loaded class and reset
        mobileNavWrapper.classList.remove("lazy-loaded");

        // Reinitialize the lazy loader
        new MobileNavLazyLoader();
      }
    }
  }, 250);
});

// Export for potential use by other scripts
window.MobileNavLazyLoader = MobileNavLazyLoader;
